import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle, Eye, Send } from 'lucide-react';
import toast from 'react-hot-toast';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../components/Common/PageHeader';
import EmailTemplateEditor, {
  EMAIL_TEMPLATE_SAMPLE_VALUES,
  interpolateEmailTemplate,
} from '../../components/EmailTemplateEditor';
import emailAdminService from '../../services/emailAdminService';
import { useAuth } from '../../contexts/AuthContext';
import { isSuperAdminUser } from '../../utils/platformRoles';
import { getAppHomePath } from '../../constants/navigation';
import { userFacingError } from '../../i18n/userFacingError';

function buildSampleVariables(variableNames) {
  return Object.fromEntries(
    (variableNames || []).map((v) => [
      v,
      EMAIL_TEMPLATE_SAMPLE_VALUES[v] ?? `[${v}]`,
    ]),
  );
}

export default function EmailTemplates() {
  const { t } = useTranslation(['admin', 'common']);
  const { user } = useAuth();
  const [types, setTypes] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ template_type: '', name: '', subject: '', body_html: '', is_active: false });
  const [serverPreview, setServerPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [catalog, list] = await Promise.all([
        emailAdminService.listTemplateTypes(),
        emailAdminService.listTemplates(filterType || undefined),
      ]);
      setTypes(catalog);
      setItems(list);
      if (filterType) {
        const active = list.find((tpl) => tpl.is_active) || list[0];
        if (active) {
          setEditingId(active.id);
          setForm({
            template_type: active.template_type,
            name: active.name,
            subject: active.subject,
            body_html: active.body_html,
            is_active: active.is_active,
          });
        }
      }
    } catch (e) {
      toast.error(userFacingError(e, 'admin:loadTemplatesError'));
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => { load(); }, [load]);

  if (!isSuperAdminUser(user)) {
    return <Navigate to={getAppHomePath(user)} replace />;
  }

  const selectedType = filterType;
  const currentVars = types.find((x) => x.type === selectedType)?.variables || [];
  const selectedTypeMeta = types.find((x) => x.type === selectedType);

  const handleTypeChange = (value) => {
    setFilterType(value);
    setServerPreview(null);
    setEditingId(null);
    if (!value) {
      setForm({ template_type: '', name: '', subject: '', body_html: '', is_active: false });
      return;
    }
    setForm({
      template_type: value,
      name: '',
      subject: '',
      body_html: '',
      is_active: false,
    });
  };

  const save = async () => {
    if (!form.template_type) {
      toast.error(t('admin:templateTypeRequired'));
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await emailAdminService.updateTemplate(editingId, form);
        toast.success(t('admin:templateUpdated'));
      } else {
        await emailAdminService.createTemplate(form);
        toast.success(t('admin:templateCreated'));
      }
      setServerPreview(null);
      await load();
    } catch (e) {
      toast.error(userFacingError(e, 'admin:saveTemplateError'));
    } finally {
      setSaving(false);
    }
  };

  const doPreview = async () => {
    setPreviewLoading(true);
    try {
      const r = await emailAdminService.previewTemplate({
        subject: form.subject,
        body_html: form.body_html,
        variables: buildSampleVariables(currentVars),
        template_type: form.template_type,
      });
      setServerPreview(r);
      toast.success(t('admin:previewUpdated'));
    } catch (e) {
      toast.error(userFacingError(e, 'admin:previewError'));
    } finally {
      setPreviewLoading(false);
    }
  };

  const activate = async (id) => {
    await emailAdminService.activateTemplate(id);
    toast.success(t('admin:templateActivated'));
    await load();
  };

  const testTpl = async (id, templateType) => {
    if (!testEmail) {
      toast.error(t('admin:testEmailRequired'));
      return;
    }
    const varsForType = types.find((x) => x.type === templateType)?.variables || [];
    try {
      const r = await emailAdminService.testTemplate(id, testEmail, buildSampleVariables(varsForType));
      toast[r.success ? 'success' : 'error'](r.success ? t('admin:testSent') : t('admin:testEmailError'));
    } catch (e) {
      toast.error(userFacingError(e, 'admin:testEmailError'));
    }
  };

  const startEdit = (tpl) => {
    setFilterType(tpl.template_type);
    setEditingId(tpl.id);
    setForm({
      template_type: tpl.template_type,
      name: tpl.name,
      subject: tpl.subject,
      body_html: tpl.body_html,
      is_active: tpl.is_active,
    });
    setServerPreview(null);
  };

  const resetForm = () => {
    setEditingId(null);
    setForm({
      template_type: filterType,
      name: '',
      subject: '',
      body_html: '',
      is_active: false,
    });
    setServerPreview(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <span className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('admin:emailTemplatesTitle')}
        description={t('admin:emailTemplatesDescription')}
      />

      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm space-y-4">
        <div>
          <label className="text-xs font-medium text-gray-600 mb-1 block">{t('admin:templateType')}</label>
          <select className="form-input" value={filterType} onChange={(e) => handleTypeChange(e.target.value)}>
            <option value="">{t('admin:selectType')}</option>
            {types.map((typeItem) => (
              <option key={typeItem.type} value={typeItem.type}>{typeItem.label}</option>
            ))}
          </select>
        </div>

        {!selectedType ? (
          <p className="text-sm text-gray-500 py-4">{t('admin:chooseTypeHint')}</p>
        ) : (
          <>
            {selectedTypeMeta && (
              <p className="text-xs text-gray-500">
                {t('admin:typeLabel')} <span className="font-medium text-gray-700">{selectedTypeMeta.label}</span>
                {currentVars.length > 0 && (
                  <span>{t('admin:variablesLabel', { vars: currentVars.map((v) => `{{ ${v} }}`).join(', ') })}</span>
                )}
              </p>
            )}

            <input
              className="form-input"
              placeholder={t('admin:internalName')}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />

            <EmailTemplateEditor
              key={editingId || selectedType}
              bodyHtml={form.body_html}
              onBodyHtmlChange={(v) => {
                setForm((f) => ({ ...f, body_html: v }));
                setServerPreview(null);
              }}
              subject={form.subject}
              onSubjectChange={(v) => {
                setForm((f) => ({ ...f, subject: v }));
                setServerPreview(null);
              }}
              variables={currentVars}
              serverPreviewHtml={serverPreview?.body_html ?? null}
              serverPreviewSubject={serverPreview?.subject ?? null}
            />

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
              />
              {t('admin:activateOnSave')}
            </label>

            <div className="flex flex-wrap gap-2">
              <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
                {t('common:save', { defaultValue: 'Salvar' })}
              </button>
              {editingId && (
                <button type="button" className="btn btn-outline" onClick={resetForm} disabled={saving}>
                  {t('admin:newTemplate')}
                </button>
              )}
              <button type="button" className="btn btn-outline" onClick={doPreview} disabled={previewLoading}>
                <Eye className="w-4 h-4" />
                {previewLoading ? t('admin:rendering') : t('admin:validatePreview')}
              </button>
            </div>
          </>
        )}
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-800 mb-3">
          {t('admin:registeredTemplates')}
          {selectedType && selectedTypeMeta ? ` — ${selectedTypeMeta.label}` : ''}
        </h3>
        <input
          className="form-input mb-4"
          placeholder={t('admin:testEmail')}
          value={testEmail}
          onChange={(e) => setTestEmail(e.target.value)}
        />
        <div className="space-y-3">
          {items.length === 0 && (
            <p className="text-sm text-gray-500">
              {selectedType ? t('admin:emptyTemplatesType') : t('admin:selectTypeToSee')}
            </p>
          )}
          {items.map((tpl) => (
            <div key={tpl.id} className="flex flex-wrap justify-between gap-2 border-b pb-3">
              <div>
                <div className="font-medium">
                  {tpl.name}
                  {tpl.is_active && <span className="text-green-600 text-xs ml-1">{t('admin:activeMark')}</span>}
                </div>
                <div className="text-sm text-gray-600">
                  {types.find((x) => x.type === tpl.template_type)?.label || tpl.template_type}
                </div>
                <div className="text-xs text-gray-500 mt-1 truncate max-w-md">
                  {t('admin:subjectLabel')} {interpolateEmailTemplate(tpl.subject, buildSampleVariables(types.find((x) => x.type === tpl.template_type)?.variables))}
                </div>
              </div>
              <div className="flex gap-2">
                {!tpl.is_active && (
                  <button type="button" className="btn btn-outline btn-sm" onClick={() => activate(tpl.id)} title={t('admin:activate')}>
                    <CheckCircle className="w-4 h-4" />
                  </button>
                )}
                <button type="button" className="btn btn-outline btn-sm" onClick={() => testTpl(tpl.id, tpl.template_type)} title={t('admin:sendTest')}>
                  <Send className="w-4 h-4" />
                </button>
                <button type="button" className="btn btn-outline btn-sm" onClick={() => startEdit(tpl)}>
                  {t('common:edit', { defaultValue: 'Editar' })}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
